import {
  Box,
  BoxSx,
  FLAG_ICONS,
  FlagIcon,
  FlagIconName,
  FlagIconNameType,
  Target,
  Text,
  ThemeOverride
} from "@modernatx-internal/component-library";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";
import { useAvailableCountries } from "@/hooks/useAvailableCountries";
import { useAvailableLanguages } from "@/hooks/useAvailableLanguages";
import { getCountryName } from "@/utils/getCountryName";
import { languageCodeMap } from "@/utils/language";

import { ContentComponentProps, LocaleSelectorContent } from "../types";
import { CountryModal, LanguageModal } from "./LocaleSelectorModals";

export const isValidIconCode = (code: string): code is FlagIconNameType => {
  return code.toUpperCase() in FlagIconName;
};

export const getFlagIcon = (countryCode: string): FlagIconNameType | undefined => {
  const upperCaseCode = countryCode.toUpperCase() as FlagIconNameType;
  if (isValidIconCode(upperCaseCode)) {
    return upperCaseCode;
  }
  return undefined;
};

export interface SplitButtonProps {
  endText?: string;
  onEndButtonClick?: () => void;
  onStartButtonClick?: () => void;
  startIcon?: keyof typeof FLAG_ICONS;
  startText?: string;
  size?: "sm" | "md";
}

export const SplitButton: React.FC<SplitButtonProps> = ({
  startIcon,
  startText,
  endText,
  onStartButtonClick,
  onEndButtonClick,
  size
}) => {
  const targetStyles: BoxSx = {
    alignItems: "center",
    borderColor: "transparent",
    borderRadius: "radius-2",
    borderStyle: "solid",
    borderWidth: 1,
    display: "flex",
    fontSize: "font-size-3",
    flexShrink: 0,
    textDecoration: "underline",
    textDecorationColor: "transparent",
    transition: "transition-color-medium",
    py: size === "sm" ? 0 : 4,
    ":hover": {
      textDecorationColor: "inherit"
    },
    ":active": {
      textDecorationColor: "inherit"
    },
    ":focus-visible": {
      borderColor: "focus"
    }
  };
  const StartTargetElement = onStartButtonClick ? Target : Box;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <StartTargetElement sx={{ gap: 3, ...targetStyles }} onClick={onStartButtonClick}>
        {startIcon ? <FlagIcon flag={startIcon} size="xs" /> : null}
        {startText && <Text weight="bold">{startText}</Text>}
      </StartTargetElement>
      {endText && startText && (
        <Box
          sx={{
            borderColor: "text-default",
            borderStyle: "solid",
            height: 4,
            borderWidth: "0 0 0 1px"
          }}
        />
      )}
      {endText && (
        <Target sx={{ ...targetStyles }} onClick={onEndButtonClick}>
          {endText}
        </Target>
      )}
    </Box>
  );
};

export const LocaleSelector: React.FC<ContentComponentProps<LocaleSelectorContent>> = ({
  countryModal,
  languageModal,
  size
}) => {
  const { country: countryCode, language } = useExperience();
  const availableLanguages = useAvailableLanguages();
  const availableCountries = useAvailableCountries();
  const [countryModalVisible, setCountryModalVisible] = React.useState(false);
  const [languageModalVisible, setLanguageModalVisible] = React.useState(false);
  const handleCountryModalDismiss = React.useCallback(() => setCountryModalVisible(false), []);
  const handleCountryModalOpen = React.useCallback(() => setCountryModalVisible(true), []);
  const handleLanguageModalDismiss = React.useCallback(() => setLanguageModalVisible(false), []);
  const handleLanguageModalOpen = React.useCallback(() => setLanguageModalVisible(true), []);
  const countryName = React.useMemo(() => {
    if (!countryCode) {
      return undefined;
    }
    return getCountryName(countryCode, language);
  }, [countryCode, language]);
  const startIcon = countryCode ? getFlagIcon(countryCode) : undefined;
  const currentLanguageName = language ? languageCodeMap[language] : "";
  const countryModalAvailable = !!(countryName && countryModal && !!availableCountries.size);
  const languageModalAvailable = !!(
    currentLanguageName &&
    languageModal &&
    !!availableLanguages.size
  );

  return (
    <>
      {(availableCountries.size >= 1 || availableLanguages.size >= 1) && (
        <SplitButton
          endText={languageModal && availableLanguages.size >= 1 ? currentLanguageName : undefined}
          startIcon={countryModalAvailable || languageModalAvailable ? startIcon : undefined}
          startText={countryModal ? countryName : undefined}
          size={size}
          onEndButtonClick={languageModalAvailable ? () => handleLanguageModalOpen() : undefined}
          onStartButtonClick={countryModalAvailable ? () => handleCountryModalOpen() : undefined}
        />
      )}
      <ThemeOverride mode="light">
        {countryModalAvailable && (
          <CountryModal
            allowCountries={countryModal.allowCountries}
            isOpen={countryModalVisible}
            noResultsText={countryModal.noResultsText}
            searchLabel={countryModal.searchLabel}
            title={countryModal?.title}
            onDismiss={handleCountryModalDismiss}
          />
        )}
        {languageModalAvailable && (
          <LanguageModal
            isOpen={languageModalVisible}
            title={languageModal?.title}
            onDismiss={handleLanguageModalDismiss}
          />
        )}
      </ThemeOverride>
    </>
  );
};
